import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Checkbox,
  FormControlLabel,
  FormGroup,
  CircularProgress,
} from '@mui/material';
import { fetchChatCategories } from '../http/categoryAPI';
import { getCategoriesForAdmin, setChatCategories } from '../http/adminChatCategoryAccessAPI';

const ChatCategoryAccessControl = ({ adminId, onCategoriesChange }) => {
  const [allCategories, setAllCategories] = useState([]);
  const [selectedCategoryIds, setSelectedCategoryIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    loadData();
  }, [adminId]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Загружаем все категории
      const categories = await fetchChatCategories();
      setAllCategories(categories);
      
      // Загружаем категории админа (может быть пустой массив)
      try {
        const adminCategories = await getCategoriesForAdmin(adminId);
        const adminCategoryIds = adminCategories.map(cat => cat.id);
        setSelectedCategoryIds(adminCategoryIds);
        
        // Уведомляем родителя о выбранных категориях
        if (onCategoriesChange) {
          onCategoriesChange(adminCategoryIds);
        }
      } catch (error) {
        // Если нет доступов - оставляем пустой массив
        setSelectedCategoryIds([]);
        if (onCategoriesChange) {
          onCategoriesChange([]);
        }
      }
      
      setInitialized(true);
    } catch (error) {
      console.error('Ошибка при загрузке категорий:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCategoryToggle = (categoryId) => {
    setSelectedCategoryIds(prev => {
      const newIds = prev.includes(categoryId)
        ? prev.filter(id => id !== categoryId)
        : [...prev, categoryId];
      
      // Уведомляем родителя о изменении
      if (onCategoriesChange) {
        onCategoriesChange(newIds);
      }
      
      return newIds;
    });
  };

  // Сохранение при изменении (debounce для избежания лишних запросов)
  useEffect(() => {
    if (!initialized || loading) return;
    
    const timer = setTimeout(async () => {
      try {
        await setChatCategories(adminId, selectedCategoryIds);
      } catch (error) {
        console.error('Ошибка при сохранении категорий:', error);
      }
    }, 500);
    
    return () => clearTimeout(timer);
  }, [selectedCategoryIds, initialized, loading, adminId]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
        <CircularProgress size={20} />
      </Box>
    );
  }

  if (allCategories.length === 0) {
    return (
      <Box sx={{ paddingLeft: 3, paddingTop: 1 }}>
        <Typography variant="caption" color="textSecondary">
          Нет доступных категорий
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ paddingLeft: 3, paddingTop: 1 }}>
      <Typography variant="caption" color="textSecondary" sx={{ mb: 0.5, display: 'block' }}>
        Доступ к категориям:
      </Typography>
      <FormGroup>
        {allCategories.map(category => (
          <FormControlLabel
            key={category.id}
            control={
              <Checkbox
                size="small"
                checked={selectedCategoryIds.includes(category.id)}
                onChange={() => handleCategoryToggle(category.id)}
              />
            }
            label={
              <Typography variant="body2">
                {category.name}
                {category.isDefault && (
                  <Typography component="span" variant="caption" color="primary" sx={{ ml: 0.5 }}>
                    (по умолчанию)
                  </Typography>
                )}
              </Typography>
            }
          />
        ))}
      </FormGroup>
    </Box>
  );
};

export default ChatCategoryAccessControl;
